import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { IonPage, IonSpinner } from "@ionic/react";
import Page from "../components/Page";
import withNav from "../utils/hoc/withNav";
import OrderService from "../services/OrderService";
import Analytics, { TRACK_PAGE_CATEGORIES } from "../lib/anayltics";
import { NavLink } from "react-router-dom";
import AsyncContentWrapper from "../components/AsyncContentWrapper";
import { getOrderCode } from "../utils/misc";
import { PrimaryTextLight, SecondaryText } from "../components/Texts";

const Content = styled.div<any>`
	width: 100%;
	height: 100%;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: ${props => props.gap};
`;

const Tick = styled.svg`
	width: 175px;
	height: 204px;
	align-self: center;
`;

const Title = styled.div<any>`
	font-weight: 700;
	font-size: 32px;
	text-align: center;
	color: #ffffff;
	margin: ${props => props.margin};
`;

const Title2 = styled.div`
	font-weight: 400;
	font-size: 26px;
	text-align: center;
	color: #ffffff;
	margin-top: 0px;
`;

const SubTitle = styled.div<any>`
	font-style: normal;
	font-weight: normal;
	font-weight: 700;
	font-size: 14px;
	text-align: center;
	letter-spacing: 0.02em;
	color: #f2f2f2;
	margin: ${props => props.margin};
`;

const Note = styled.div`
	font-style: normal;
	font-weight: normal;
	font-weight: 400;
	font-size: 14px;
	height: 14px;
	text-align: center;
	letter-spacing: 0.02em;
	color: #f2f2f2;
	margin-top: auto;
    margin-bottom: 3rem;
`;

const BigSpace = styled.div`
	margin: 2rem 0 0 0;
`;

const Link = styled(NavLink)`
	font-style: normal;
	font-weight: normal;
	font-weight: 700;
	font-size: 14px;
	text-align: center;
	letter-spacing: 0.02em;
	color: #f2f2f2;
	text-decoration: underline;
`;

const Message = styled.div<any>`
	margin: ${props => props.margin};
	width: 85%;
	line-height: 20px;
`;

const maxRetryCount = 3;
const redirectIn = 3;

const OrderStatus: React.FC = (props: any) => {
	const { xookId, navigate } = props;

	const { orderId, kioskId } = props.match.params;

	const [loading, setLoading] = useState<any>(true);
	const [error, setError] = useState(false);
	const [success, setSuccess] = useState(false);
    const [navTimer, setNavTimer] = useState(redirectIn);

	const navTimeout = useRef<any>();
	const TimerInterval = useRef<any>();
	const Timer = useRef<any>();

	async function getOrderStatus(retryCount?: number) {
		setLoading(true);
		const orderStatusResponse: any = await OrderService.getOrderStatus(orderId);
		const { data, error } = orderStatusResponse;
		if (data) {
			const { retry, track, abort, completed } = data;
			if (retry) {
				retryCount = retryCount || 0;
				retryCount++;
				if (retryCount <= maxRetryCount) {
					setTimeout(async function () {
						await getOrderStatus(retryCount);
					}, 10 * 1000);
					return;
				} else {
					setLoading(false);
					setError(true);
				}
			}
			if (track || completed) {
				setLoading(false);
				setSuccess(true);

				TimerInterval.current = setInterval(() => {
                    setNavTimer(prev => prev - 1);
				}, 1000);
			}
			if (abort) {
				setLoading(false);
				setError(true);
			}
		}
		if (error) {
			setLoading(false);
			setError(true);
		}
	}

	function onPageLoad() {
		Analytics.trackPage(TRACK_PAGE_CATEGORIES.PAGE_LOAD, "Order Status");
		getOrderStatus();
	}

	useEffect(() => {
		onPageLoad();
	}, []);

    useEffect(() => {
        if(navTimer <= 0) {
            clearInterval(TimerInterval.current)
            navigate.push(`/kiosk/${xookId || kioskId}/order/track/${orderId}`)
        }
    }, [navTimer])

	function renderPage() {
		if (error) {
			return (
				<Content gap="1rem" >
					<Title>Sorry!</Title>
					<SubTitle>We could not place your order.</SubTitle>
					<Link
						to={`/kiosk/${kioskId}`}
						onClick={() => {
							if (TimerInterval.current) clearInterval(TimerInterval.current);
						}}>
						Go Back
					</Link>
				</Content>
			);
		}
		if (loading) {
			return (
				<Content gap="2rem" >
					<IonSpinner name="dots" color="white" />
					<Title>Placing your order</Title>
					<Message>Please don't leave this page.</Message>
				</Content>
			);
		}
		if (success) {

            const orderCode = getOrderCode(orderId);

			return (
				<Content>
					<Title margin="auto 0 35px 0">Order Placed!</Title>
					<Tick version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 130.2 130.2">
						<circle
							className="path circle"
							fill="none"
							stroke="#FFCC00"
							stroke-width="6"
							stroke-miterlimit="10"
							cx="65.1"
							cy="65.1"
							r="62.1"
						/>
						<polyline
							className="path check"
							fill="none"
							stroke="#FFCC00"
							stroke-width="6"
							stroke-linecap="round"
							stroke-miterlimit="10"
							points="100.2,40.2 51.5,88.8 29.8,67.5 "
						/>
					</Tick>
					<BigSpace />
					<Title2>Order ID #{orderCode}</Title2>
					<Message margin="1rem 0 0 0" ><SecondaryText fontSize="16px" >You can track your order from the track order page and also on the kiosk's display</SecondaryText>.</Message>
					<Note>
						<span>Redirecting you to </span>
						<Link
							to={`/kiosk/${xookId || kioskId}/order/track/${orderId}`}
							onClick={() => {
								if (navTimeout.current) clearTimeout(navTimeout.current);
							}}>
							Track Order Page
						</Link>{" "}
						<span> in {navTimer}s </span>
					</Note>
				</Content>
			);
		}
	}

	return (
		<IonPage>
			<Page>
				<>{renderPage()}</>
			</Page>
		</IonPage>
	);
};

export default withNav(OrderStatus);
